import React, {
  useEffect, useId, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';

import { LINK_COPY } from '../../../constants/abdmCopy';
import { HI_TYPE_ICONS } from '../../../constants/abdmHiTypes';
import {
  LIST, PREVIEW, ROWS, slotName,
} from '../../../constants/hipRecordsCopy';
import { ENCOUNTER_KIND, FILE_DISPOSITION, SLOT_STATE } from '../../../constants/hipRecordEnums';
import { fetchRecordFile } from '../../../services/api/hipRecordsApi';
import { canInlinePdf } from '../../../utils/browserDetect';
import { formatDateIST, formatDayIST, formatDateTimeISTWithYearNoSeconds } from '../../../utils/formatIST';
import { ReplacePencil, TAG, replaceLockReason, sourceLineOf } from './HipRecordRows';

/**
 * HipRecordPreviewModal - View of one slot of an encounter card (variant B): the file
 * (PDF in an iframe / image), "File n of m" when the slot holds several, footer Download +
 * pencil (icon only, OR-10; locked with its reason once linking/linked) + Close.
 * Props (frozen, build-plan §4.6): { open, record, slot, initialIndex, onClose, onReplace(slot) }
 *   + replaceLock (OR-82, optional): a sentence that locks the pencil on top of the slot's own lock (the card has an
 *     inline Add file panel open - one per card); tapping the locked pencil shows it.
 *
 * - Bytes come from fetchRecordFile by the slot's `slotKey` (never its record type: a card can hold
 *   several Diagnostic Reports); the server returns the Linked copy for LINKING/LINKED, OR-9.
 *   ONE fetch per file shown: the effect depends on primitives only (ids + the file's own name /
 *   type), so a re-render or a new `slot` object with the same file never refetches. Each loaded
 *   file becomes an object URL that is revoked when the file changes or the modal closes.
 * - Download saves the file already on screen (no second request).
 * - A slot with NO file is OP Consultation without a prescription (OR-19): View shows a summary of
 *   what will be linked, and there is no Download.
 * - Dismiss: Close button, Esc, click on the overlay. Layer: above the page, below ConfirmationModal (1100).
 * - Replace is the card's job: onReplace(slot) opens the file picker. This modal stays open until a file is
 *   chosen (the card closes it then); a cancelled picker changes nothing.
 */

const BTN = 'rounded-md border px-3 py-1 text-xs font-semibold whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60';
const BTN_NEUTRAL = `${BTN} border-slate-400 bg-slate-100 text-slate-800 hover:bg-slate-200 dark:border-slate-400 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-600`;
const BTN_PRIMARY = `${BTN} border-teal-700 bg-teal-700 text-white hover:bg-teal-800`;
const NOTE = 'm-0 p-6 text-center text-sm';

const when = (iso) => formatDateTimeISTWithYearNoSeconds(iso) || '—';

function PreviewDialog({
  record, slot, initialIndex, onClose, onReplace, replaceLock,
}) {
  const titleId = useId();
  const closeButton = useRef(null);
  const { files } = slot;
  const [index, setIndex] = useState(initialIndex);
  const [loaded, setLoaded] = useState(null); // { fileId, url, type, name } | { fileId, failed: true }
  const [whyOpen, setWhyOpen] = useState(false); // tap on the locked pencil (touch has no hover)

  const at = Math.max(0, Math.min(index, files.length - 1));
  const file = files[at];
  const { encounterKind, encounterId } = record;
  const { recordType, slotKey } = slot;
  const fileId = file ? file.fileId : null;
  const slotFileName = file ? file.fileName : null;
  const slotContentType = file ? file.contentType : null;

  // Esc dismisses.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus moves in on open and back to what opened it on close.
  useEffect(() => {
    const opener = document.activeElement;
    if (closeButton.current) closeButton.current.focus();
    return () => { if (opener && opener.focus) opener.focus(); };
  }, []);

  // One fetch per file shown. The cleanup revokes the object URL (file change, close), and a
  // response that arrives after the cleanup never creates one.
  useEffect(() => {
    if (!fileId) return undefined;
    let cancelled = false;
    let url = null;
    fetchRecordFile({
      encounterKind, encounterId, slotKey, fileId, disposition: FILE_DISPOSITION.INLINE,
    }).then(({ blob, fileName }) => {
      if (cancelled) return;
      url = URL.createObjectURL(blob);
      setLoaded({
        fileId, url, type: blob.type || slotContentType || '', name: fileName || slotFileName,
      });
    }).catch(() => {
      if (!cancelled) setLoaded({ fileId, failed: true });
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [encounterKind, encounterId, slotKey, fileId, slotFileName, slotContentType]);

  const current = loaded && loaded.fileId === fileId ? loaded : null; // a previous file's state is not this file's
  const isIpd = encounterKind === ENCOUNTER_KIND.IPD;
  const linked = slot.state === SLOT_STATE.LINKED;
  const linking = slot.state === SLOT_STATE.LINKING;
  const lockReason = replaceLockReason(slot) || replaceLock;
  // No file and not linked = nothing was saved yet, so no "Saved in ArogyaKhata · —" line.
  const sourceLine = file || linked ? sourceLineOf(slot) : null;
  const subline = [
    record.patientName,
    record.uhid,
    `${LIST.kinds[encounterKind]} ${isIpd ? record.ipNumber || '' : when(record.encounterAt)}`.trim(),
  ].filter(Boolean).join(' · ');

  const summary = PREVIEW.opConsultSummary;
  const summaryRows = [
    [summary.complaint, slot.summary && slot.summary.chiefComplaint],
    [summary.diagnosis, slot.summary && slot.summary.diagnosis],
    [summary.doctor, record.doctorName],
    [summary.date, record.visitDate ? formatDayIST(record.visitDate) : formatDateIST(record.encounterAt)],
  ];

  let body;
  if (!file) {
    body = (
      <div className="mx-auto max-w-[420px] rounded-md border border-slate-300 bg-white px-5 py-4 text-sm text-slate-900 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100">
        <h3 className="m-0 mb-2 text-sm font-bold">
          {linking || linked ? summary.titleLinked : summary.title}
        </h3>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          {summaryRows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className="font-semibold text-slate-600 dark:text-slate-300">{label}</dt>
              <dd className="m-0 [overflow-wrap:anywhere]">{value || '—'}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    );
  } else if (!current) {
    body = <p role="status" className={`${NOTE} text-slate-700 dark:text-slate-200`}>{LINK_COPY.panelLoading}</p>;
  } else if (current.failed || !(current.type === 'application/pdf' || current.type.startsWith('image/'))) {
    body = <p role="alert" className={`${NOTE} text-red-700 dark:text-red-300`}>{PREVIEW.loadError}</p>;
  } else if (current.type !== 'application/pdf') {
    body = <img src={current.url} alt={current.name} className="mx-auto max-h-[65vh] max-w-full object-contain" />;
  } else if (canInlinePdf) {
    body = <iframe src={current.url} title={current.name} className="block h-[65vh] min-h-[320px] w-full rounded border border-slate-300 bg-white dark:border-slate-500" />;
  } else {
    body = <p className={`${NOTE} text-slate-700 dark:text-slate-200`}>{PREVIEW.noInlinePdf}</p>;
  }

  return createPortal(
    <div
      data-testid="hip-preview-overlay"
      className="fixed inset-0 z-[1050] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog" aria-modal="true" aria-labelledby={titleId}
        className="flex max-h-[92vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-2xl border border-slate-300 bg-white text-slate-900 sm:rounded-[14px] dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100"
      >
        <div className="border-b border-slate-300 px-4 py-3 dark:border-slate-600">
          <h2 id={titleId} className="m-0 text-base font-bold">
            <span aria-hidden="true">{HI_TYPE_ICONS[recordType]}</span> {slotName(slot)}
          </h2>
          <p className="m-0 mt-0.5 text-xs text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">
            {subline}
            {sourceLine && ' · '}
            {linked
              ? <span className="font-bold text-green-800 dark:text-green-300">{sourceLine}</span>
              : sourceLine}
            {slot.replacedByStaff && <span className={TAG}>{ROWS.replacedByStaff}</span>}
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-slate-50 px-4 py-3.5 dark:bg-slate-700">
          {files.length > 1 && (
            <div className="mb-2.5 flex items-center justify-center gap-2 text-xs text-slate-700 dark:text-slate-200">
              <button type="button" className={BTN_NEUTRAL} disabled={at === 0} onClick={() => setIndex(at - 1)}>
                {PREVIEW.previous}
              </button>
              <span aria-live="polite">{PREVIEW.fileOf(at + 1, files.length)}</span>
              <button type="button" className={BTN_NEUTRAL} disabled={at === files.length - 1} onClick={() => setIndex(at + 1)}>
                {PREVIEW.next}
              </button>
            </div>
          )}
          {body}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-slate-300 px-4 py-3 dark:border-slate-600">
          {whyOpen && lockReason && (
            <p role="status" className="m-0 mr-auto text-xs text-slate-700 dark:text-slate-200">{lockReason}</p>
          )}
          {current && current.url && (
            <a href={current.url} download={current.name} className={BTN_NEUTRAL}>
              <span aria-hidden="true">⬇</span> {ROWS.download}
            </a>
          )}
          <ReplacePencil slot={slot} onReplace={onReplace} lockReason={replaceLock} onLockedTap={() => setWhyOpen((o) => !o)} />
          <button ref={closeButton} type="button" className={BTN_PRIMARY} onClick={onClose}>{PREVIEW.close}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function HipRecordPreviewModal({
  open, record, slot, initialIndex = 0, onClose, onReplace, replaceLock,
}) {
  // Mounted only while open: every open starts fresh (file index, loaded state) and an unmount
  // revokes the object URL.
  if (!open) return null;
  return (
    <PreviewDialog
      record={record} slot={slot} initialIndex={initialIndex} onClose={onClose} onReplace={onReplace} replaceLock={replaceLock}
    />
  );
}
